import { membershipIdFromFrameShape } from './frameMembership.js';

/** Page-space margin (at zoom 1) for preferring a frame border over pins on top. */
const FRAME_EDGE_BIND_PX = 22;

function distanceToRectEdge(point, bounds) {
  const right = bounds.x + bounds.w;
  const bottom = bounds.y + bounds.h;
  const dx = Math.max(bounds.x - point.x, 0, point.x - right);
  const dy = Math.max(bounds.y - point.y, 0, point.y - bottom);
  if (dx > 0 || dy > 0) {
    return Math.hypot(dx, dy);
  }
  return Math.min(point.x - bounds.x, right - point.x, point.y - bounds.y, bottom - point.y);
}

/**
 * Nearest page-level frame whose border (or title strip above) is within margin of the point.
 */
export function findFrameNearEdge(editor, point, margin) {
  const pageId = editor.getCurrentPageId();
  let best = null;
  let bestDist = margin;

  for (const shape of editor.getCurrentPageShapes() || []) {
    if (!shape || shape.type !== 'frame' || shape.parentId !== pageId) continue;
    if (shape.isLocked) continue;

    const bounds = editor.getShapePageBounds(shape);
    if (!bounds || bounds.w < 2 || bounds.h < 2) continue;

    // Title sits just above the frame body — treat that strip as bindable frame chrome.
    const titleStrip = {
      x: bounds.x,
      y: bounds.y - Math.min(28, margin * 1.25),
      w: bounds.w,
      h: Math.min(28, margin * 1.25) + 4,
    };
    const inTitle =
      point.x >= titleStrip.x
      && point.x <= titleStrip.x + titleStrip.w
      && point.y >= titleStrip.y
      && point.y <= titleStrip.y + titleStrip.h;

    const dist = inTitle ? 0 : distanceToRectEdge(point, bounds);
    if (dist <= bestDist) {
      bestDist = dist;
      best = shape;
    }
  }

  return best;
}

function isArrowTargeting(editor) {
  const path = String(editor.getPath?.() || '');
  if (path.startsWith('arrow.')) return true;
  if (
    path.includes('dragging_handle')
    || path.includes('pointing_handle')
    || path.includes('translating')
  ) {
    const only = editor.getOnlySelectedShape?.();
    return only?.type === 'arrow';
  }
  return false;
}

function shouldPreferFrameOverHit(hit, frame) {
  if (!hit || hit.id === frame.id) return true;
  if (hit.type === 'frame') return false;

  if (
    hit.type === 'pin-card'
    || hit.type === 'suggest-pin'
    || hit.type === 'conversation-pin'
  ) {
    const membershipId = membershipIdFromFrameShape(frame);
    const pinFrameId = hit.meta?.frameId;
    if (membershipId && pinFrameId && String(pinFrameId) === String(membershipId)) {
      return true;
    }
    // Pins often sit flush against frame borders; prefer the frame when on the edge.
    return true;
  }

  // Notes / geo / text parked on the border — allow snapping the connector to the frame.
  return hit.type === 'note' || hit.type === 'text' || hit.type === 'geo';
}

/**
 * Soft-membership boards keep pins above frames, so stock arrow hit-testing rarely
 * binds to a frame. While the arrow tool (or an arrow handle) is active, prefer a
 * nearby frame border/title over overlapping pins.
 *
 * @returns {() => void} unregister
 */
export function registerFrameArrowTargeting(editor) {
  if (!editor || typeof editor.getShapeAtPoint !== 'function') return () => {};

  const original = editor.getShapeAtPoint.bind(editor);

  editor.getShapeAtPoint = (point, opts = {}) => {
    const hit = original(point, opts);

    if (!isArrowTargeting(editor)) return hit;
    // Arrow targeting always passes hitInside / hitFrameInside.
    if (!opts.hitInside && !opts.hitFrameInside) return hit;

    const zoom = editor.getZoomLevel?.() || 1;
    const margin = FRAME_EDGE_BIND_PX / zoom;
    const frame = findFrameNearEdge(editor, point, margin);
    if (!frame) return hit;
    if (!shouldPreferFrameOverHit(hit, frame)) return hit;

    if (typeof opts.filter === 'function' && !opts.filter(frame)) return hit;
    return frame;
  };

  return () => {
    editor.getShapeAtPoint = original;
  };
}
